import {
	isManyProperty,
	type Property,
	type PropertyID,
	PropertyTypes,
	type PropertyValue,
} from "@ext/properties/models";

export const UNGROUPED = null;
export const FLAG_SET = "yes";
export const FLAG_UNSET = "none";

export interface GroupableArticle {
	properties: PropertyValue[];
}

export interface GroupOptions {
	keepEmptyGroups?: boolean;
	excludedValues?: string[];
	valueOrder?: Record<PropertyID, string[]>;
}

export interface ArticleGroup<T> {
	group: (string | null)[];
	groupBy?: PropertyID;
	groupValues?: string[];
	articles: T[];
	subgroups?: ArticleGroup<T>[];
}

interface LevelKey {
	key: string | null;
	values: string[];
}

const rawLevels = (article: GroupableArticle, propertyId: PropertyID, type: PropertyTypes): LevelKey[] => {
	if (type === PropertyTypes.flag) {
		const isSet = !!article.properties?.some((p) => p.id === propertyId);
		return [{ key: isSet ? FLAG_SET : FLAG_UNSET, values: isSet ? [FLAG_SET] : [] }];
	}
	if (type === PropertyTypes.blockMd) return [{ key: UNGROUPED, values: [] }];

	const value = article.properties?.find((p) => p.id === propertyId)?.value;
	if (!value?.length) return [{ key: UNGROUPED, values: [] }];

	if (isManyProperty[type]) return value.map((single) => ({ key: single, values: [single] }));
	return [{ key: value[0], values: [...value] }];
};

const orderGroups = <T>(groups: ArticleGroup<T>[], values: string[]): ArticleGroup<T>[] => {
	const rank = new Map(values.map((value, index) => [value, index]));
	return groups.sort((a, b) => {
		const aKey = a.group[0];
		const bKey = b.group[0];
		if (aKey === UNGROUPED) return bKey === UNGROUPED ? 0 : 1;
		if (bKey === UNGROUPED) return -1;
		return (rank.get(aKey) ?? values.length) - (rank.get(bKey) ?? values.length);
	});
};

const groupArticles = <T extends GroupableArticle>(
	articles: T[],
	groupBy: PropertyID[],
	catalogProps: Map<PropertyID, Property>,
	options: GroupOptions = {},
	level = 0,
): ArticleGroup<T>[] => {
	const levels = groupBy.filter((id) => catalogProps.has(id));
	if (level >= levels.length) return [{ group: [], articles, subgroups: [] }];

	const propertyId = levels[level];
	const property = catalogProps.get(propertyId);
	const buckets = new Map<string | null, T[]>();
	const bucketValues = new Map<string | null, string[]>();

	if (options.keepEmptyGroups)
		for (const value of property.values ?? [])
			if (!options.excludedValues?.includes(value)) {
				buckets.set(value, []);
				bucketValues.set(value, [value]);
			}

	for (const article of articles)
		for (const { key, values } of rawLevels(article, propertyId, property.type)) {
			const bucket = buckets.get(key);
			if (bucket) bucket.push(article);
			else buckets.set(key, [article]);
			if (!bucketValues.has(key)) bucketValues.set(key, values);
		}

	if (buckets.size === 0 && articles.length) {
		buckets.set(UNGROUPED, []);
		bucketValues.set(UNGROUPED, []);
	}

	const groups = Array.from(buckets, ([key, bucketArticles]) => ({
		group: [key],
		groupBy: propertyId,
		groupValues: bucketValues.get(key) ?? (key === UNGROUPED ? [] : [key]),
		articles: [] as T[],
		subgroups: groupArticles(bucketArticles, groupBy, catalogProps, options, level + 1),
	}));

	return orderGroups(groups, options.valueOrder?.[propertyId] ?? property.values ?? []);
};

export default groupArticles;
